import React from 'react';
import moment from 'moment-timezone';
import Grid from '@material-ui/core/Grid';
import { css, StyleSheet } from 'aphrodite';
import { Line } from 'react-chartjs-2';

import { BaseEntityPage } from '../../common/components/BaseEntityPage';
import { Helper } from '../../common/helpers/Helper';
import { ApiHelper } from '../../common/helpers/ApiHelper';
import { GraphHelper } from '../../common/components/charts/GraphHelper';
import { AppConfig } from '../../AppConfig';
import { AppTheme } from '../../styles/AppTheme';
import { PlaybackHistoryFilterBar } from '../../components/PlaybackHistoryFilterBar';
import { Toast } from '../../components/Toast';
import { VenueSummary2 } from '../../components/VenueSummary2';
import { VenueHealthDashboard } from './VenueHealthDashboard';

const queryString = require('query-string');
const MAX_DATE_RANGE_DAYS = 24;

const getDefaultDateRange = () => {
  const endDate = moment()
    .tz(AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO)
    .subtract(1, 'day');

  return {
    dateStart: endDate.clone().subtract(6, 'days').format('YYYY-MM-DD'),
    dateEnd: endDate.format('YYYY-MM-DD')
  };
};

export class ScreenHealthTimeseriesReport extends BaseEntityPage {
  noAdd = true;

  constructor(props, options) {
    super(props, options);
    const query = queryString.parse(props.location.search);
    const defaultDateRange = getDefaultDateRange();
    const dateStart = this.getValidQueryDate(query.startDate) || defaultDateRange.dateStart;
    const dateEnd = this.getValidQueryDate(query.endDate) || defaultDateRange.dateEnd;
    const screen = this.getScreenFromLocation(props.location.search);

    this.embedded = Boolean(props.embedded);
    this.routePath = props.routePathOverride || '/admin/screen-health-timeseries-report';
    this.pageKey = 'ScreenHealthTimeseriesReport';
    this.pageTitle = Helper.getString('screenHealthTimeseriesReport');
    this.venueCode = screen;
    this.state = {
      ...this.state,
      fetchState: screen ? ApiHelper.State.LOADING : ApiHelper.State.READY,
      screen,
      dateStart,
      dateEnd,
      inProcessFilter: false,
      hasCompletedReportRequest: false,
      showToast: false,
      toastMsg: '',
      timeseriesData: {},
      seriesItems: [],
      device: null
    };
    this.toFetch = screen ? this.makeFetches(screen, dateStart, dateEnd) : [];
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: Helper.getString('screenHealthTimeseriesReport') }
    ];
  }

  componentDidMount() {
    if (this.state.screen) this.fetchReport();
  }

  readyView() {
    if (this.embedded) return this.renderAboveTable();
    return super.readyView();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search === this.props.location.search) return;

    const query = queryString.parse(this.props.location.search);
    const defaultDateRange = getDefaultDateRange();
    const nextState = {
      screen: this.getScreenFromLocation(this.props.location.search),
      dateStart: this.getValidQueryDate(query.startDate) || defaultDateRange.dateStart,
      dateEnd: this.getValidQueryDate(query.endDate) || defaultDateRange.dateEnd,
      inProcessFilter: false,
      showToast: false,
      toastMsg: ''
    };

    if (
      nextState.screen !== this.state.screen ||
      nextState.dateStart !== this.state.dateStart ||
      nextState.dateEnd !== this.state.dateEnd
    ) {
      this.setState(nextState, this.fetchReport);
    }
  }

  getValidQueryDate = (value) => {
    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  };

  getScreenFromLocation = (search) => {
    const query = queryString.parse(search);
    return (query.screen || query.ddCode || query.venueCode || '').trim();
  };

  makeFetches = (screen = this.state.screen, dateStart = this.state.dateStart, dateEnd = this.state.dateEnd) => {
    return [{
      key: 'venueHealthTimeseriesReport',
      method: 'GET',
      paths: ['miscs', 'venue-health-timeseries-report'],
      queryParams: {
        venueCode: screen,
        startDate: dateStart,
        endDate: dateEnd
      }
    }];
  };

  fetchReport = ({ inline = false } = {}) => {
    const { screen, dateStart, dateEnd } = this.state;
    this.venueCode = screen;

    if (!screen) {
      this.toFetch = [];
      this.setState({
        fetchState: ApiHelper.State.READY,
        timeseriesData: {},
        seriesItems: [],
        device: null,
        inProcessFilter: false,
        hasCompletedReportRequest: false
      });
      return;
    }

    const dateRangeError = this.getDateRangeError(dateStart, dateEnd);
    if (dateRangeError) {
      this.toFetch = [];
      this.setState({
        fetchState: ApiHelper.State.READY,
        timeseriesData: {},
        seriesItems: [],
        device: null,
        inProcessFilter: false,
        showToast: true,
        toastMsg: dateRangeError
      });
      return;
    }

    this.toFetch = this.makeFetches(screen, dateStart, dateEnd);
    if (inline) {
      this.fetchReportData();
    } else {
      this.fetchItems();
    }
  };

  fetchReportData = () => {
    ApiHelper.call(
      this.makeFetches()[0],
      function (err, data) {
        if (err) {
          this.setState({
            inProcessFilter: false,
            hasCompletedReportRequest: true,
            showToast: true,
            toastMsg: Helper.getErrorMsg(err)
          });
          return;
        }

        this.onFetchSuccess([data]);
        this.setState({ inProcessFilter: false });
      }.bind(this)
    );
  };

  onFetchSuccess(results) {
    const result = results[0] || {};
    const items = (result.items || []).slice().sort((a, b) => {
      return new Date(a.created_ts).getTime() - new Date(b.created_ts).getTime();
    });
    const device = result.device || {
      deviceCode: String(result.venueCode || this.state.screen || '').replace(/_/g, '-')
    };

    this.setState({
      items: [],
      timeseriesData: result,
      seriesItems: items,
      device,
      hasCompletedReportRequest: true
    });
  }

  formatTsLabel = (createdTs) => {
    if (!createdTs) return '';
    return Helper.formatDate2({
      utcTimestamp: new Date(createdTs).getTime(),
      format: 'MM/DD/YYYY hh:mm a',
      zone: 'Etc/UTC'
    });
  };

  normalizeChartValue = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    return value;
  };

  normalizeNetworkType = (networkType) => {
    return (networkType || '').toString().trim().toUpperCase();
  };

  getDataUsageNetworkType = (item) => {
    const networkType = this.normalizeNetworkType(item.network_type);
    if (networkType === 'WIFI') return 'wifi';
    if (networkType === 'ETHERNET') return 'ethernet';
    if (networkType === 'MOBILE' || networkType === 'CELLULAR') return 'mobile';
    return null;
  };

  getDataUsageValue = (item, direction) => {
    if (this.isDeviceOffline(item)) return 0;
    const networkType = this.getDataUsageNetworkType(item);
    if (!networkType) return null;
    return item[`data_${direction}_24h_type_${networkType}_gb`];
  };

  isStatusChart = (chartType) => chartType === 'connectivity_status';

  getConnectivityStatusValue = (lastActivityMin) => {
    const minutes = Number(lastActivityMin);
    if (isNaN(minutes)) return null;
    if (minutes < 10) return 2;
    if (minutes < 1440) return 1;
    return 0;
  };

  isDeviceOffline = (item) => this.getConnectivityStatusValue(item.last_activity_min) === 0;

  getOfflineAdjustedValue = (item, value) => this.isDeviceOffline(item) ? 0 : value;

  getConnectivityStatusLabel = (value) => {
    if (Number(value) === 2) return 'Online';
    if (Number(value) === 1) return 'Standby';
    return 'Offline';
  };

  buildLineChart = ({ chartTitle, chartType, description, valueMapper, color }) => {
    const items = this.state.seriesItems || [];
    if (!items.length) return null;

    const labels = items.map((item) => this.formatTsLabel(item.created_ts));
    const data = items.map((item) => this.normalizeChartValue(valueMapper(item)));
    const hasValue = data.some((value) => value !== null && value !== undefined);
    if (!hasValue) return null;

    const chartData = GraphHelper.generateLineGraphData({ chartTitle, labels, data });
    chartData.datasets[0].borderColor = color;
    chartData.datasets[0].backgroundColor = color;
    chartData.datasets[0].pointBackgroundColor = color;
    chartData.datasets[0].pointBorderColor = color;
    chartData.datasets[0].pointRadius = 3;
    chartData.datasets[0].pointHoverRadius = 5;
    chartData.datasets[0].borderWidth = 3;

    if (this.isStatusChart(chartType)) {
      chartData.datasets[0].steppedLine = true;
      chartData.datasets[0].pointRadius = 2;
      chartData.datasets[0].pointHoverRadius = 4;
    }

    return {
      chartTitle,
      chartType,
      description,
      data: chartData,
      options: this.getLineChartOptions(chartType)
    };
  };

  buildMultiLineChart = ({ chartTitle, description, series, items: chartItems }) => {
    const items = chartItems === undefined ? (this.state.seriesItems || []) : chartItems;
    if (!items.length) return null;

    const labels = items.map((item) => this.formatTsLabel(item.created_ts));
    const datasets = series.map((currentSeries) => {
      const data = items.map((item) => this.normalizeChartValue(currentSeries.valueMapper(item)));
      return {
        label: currentSeries.label,
        data,
        fill: false,
        borderColor: currentSeries.color,
        backgroundColor: currentSeries.color,
        pointBackgroundColor: currentSeries.color,
        pointBorderColor: currentSeries.color,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 3
      };
    }).filter((dataset) => dataset.data.some((value) => value !== null && value !== undefined));

    if (!datasets.length) return null;
    return {
      chartTitle,
      description,
      data: { labels, datasets },
      options: this.getLineChartOptions()
    };
  };

  getDailyLatestItems = () => {
    const latestItemsByDay = (this.state.seriesItems || []).reduce((result, item) => {
      const createdAt = moment.utc(item.created_ts);
      if (!createdAt.isValid()) return result;

      const day = createdAt.format('YYYY-MM-DD');
      const latestItem = result[day];
      if (!latestItem || createdAt.valueOf() >= moment.utc(latestItem.created_ts).valueOf()) {
        result[day] = item;
      }
      return result;
    }, {});

    return Object.keys(latestItemsByDay)
      .sort()
      .map((day) => latestItemsByDay[day]);
  };

  getLineChartOptions = (chartType) => {
    const baseOptions = GraphHelper.getLineChartOptions();
    const options = {
      ...baseOptions,
      maintainAspectRatio: false,
      legend: {
        ...baseOptions.legend,
        labels: {
          ...baseOptions.legend.labels,
          fontSize: 11
        }
      },
      layout: {
        autoPadding: false,
        padding: { top: 0, bottom: 18, left: 0, right: 10 }
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem) => {
            const rawValue = Number(tooltipItem.value || 0);
            if (this.isStatusChart(chartType)) return this.getConnectivityStatusLabel(rawValue);
            const datasetLabel = tooltipItem.datasetIndex !== undefined && tooltipItem.chart && tooltipItem.chart.data.datasets[tooltipItem.datasetIndex]
              ? tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].label
              : '';
            return datasetLabel ? `${datasetLabel}: ${rawValue}` : `${rawValue}`;
          }
        }
      },
      scales: {
        xAxes: [{
          gridLines: {
            display: true,
            color: 'rgba(148, 163, 184, 0.18)',
            drawBorder: false
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 8,
            maxRotation: 0,
            minRotation: 0,
            padding: 12,
            fontSize: 10
          }
        }],
        yAxes: [{
          gridLines: {
            display: true,
            color: 'rgba(148, 163, 184, 0.18)',
            drawBorder: false
          },
          ticks: {
            padding: 10,
            fontSize: 10,
            callback: (value) => `${value}`
          }
        }]
      }
    };

    if (this.isStatusChart(chartType)) {
      options.scales.yAxes[0].ticks = {
        ...options.scales.yAxes[0].ticks,
        min: 0,
        max: 2,
        stepSize: 1,
        padding: 16,
        callback: (value) => this.getConnectivityStatusLabel(value)
      };
    } else {
      options.scales.yAxes[0].ticks = {
        ...options.scales.yAxes[0].ticks,
        precision: chartType === 'heartbeat' ? 0 : 2,
        beginAtZero: chartType === 'heartbeat'
      };
    }

    return options;
  };

  buildChartsData = () => {
    const items = this.state.seriesItems || [];
    const hasWifiSignal = items.some((item) => this.normalizeNetworkType(item.network_type) === 'WIFI' && (this.isDeviceOffline(item) || (item.wifi_signal_strength !== null && item.wifi_signal_strength !== undefined)));
    const hasWifiSpeed = items.some((item) => this.normalizeNetworkType(item.network_type) === 'WIFI' && (this.isDeviceOffline(item) || (item.wifi_speed !== null && item.wifi_speed !== undefined)));
    const hasCellularSignal = items.some((item) => this.normalizeNetworkType(item.network_type) === 'CELLULAR' && (this.isDeviceOffline(item) || (item.cellular_signal_strength !== null && item.cellular_signal_strength !== undefined)));

    return [
      this.buildLineChart({
        chartTitle: 'Heartbeat',
        chartType: 'heartbeat',
        valueMapper: (item) => item.hb === null || item.hb === undefined ? 0 : item.hb,
        color: '#7c3aed'
      }),
      // this.buildLineChart({
      //   chartTitle: 'Connectivity Status (Online / Standby / Offline)',
      //   chartType: 'connectivity_status',
      //   valueMapper: (item) => this.getConnectivityStatusValue(item.last_activity_min),
      //   color: '#1d4ed8'
      // }),
      this.buildMultiLineChart({
        chartTitle: 'Storage (Total / Available GB)',
        series: [
          { label: 'Available Storage (GB)', valueMapper: (item) => item.free_gb, color: '#ea580c' },
          { label: 'Total Storage (GB)', valueMapper: (item) => item.total_gb, color: '#2563eb' }
        ]
      }),
      this.buildMultiLineChart({
        chartTitle: 'RAM (Total / Available GB)',
        series: [
          { label: 'Available RAM (GB)', valueMapper: (item) => item.device_ram_avail_mem, color: '#059669' },
          { label: 'Total RAM (GB)', valueMapper: (item) => item.device_ram_total_mem, color: '#7c3aed' }
        ]
      }),
      hasWifiSignal && this.buildLineChart({
        chartTitle: 'WiFi Signal Strength (0-4 Bars)',
        valueMapper: (item) => this.normalizeNetworkType(item.network_type) === 'WIFI'
          ? this.getOfflineAdjustedValue(item, item.wifi_signal_strength)
          : null,
        color: '#0f766e'
      }),
      hasWifiSpeed && this.buildLineChart({
        chartTitle: 'WiFi Speed (0 to Max Available Mbps)',
        valueMapper: (item) => this.normalizeNetworkType(item.network_type) === 'WIFI'
          ? this.getOfflineAdjustedValue(item, item.wifi_speed)
          : null,
        color: '#b45309'
      }),
      hasCellularSignal && this.buildLineChart({
        chartTitle: 'Cellular Signal Strength (0-4 Bars)',
        valueMapper: (item) => this.normalizeNetworkType(item.network_type) === 'CELLULAR'
          ? this.getOfflineAdjustedValue(item, item.cellular_signal_strength)
          : null,
        color: '#dc2626'
      }),
      this.buildMultiLineChart({
        chartTitle: 'Data Usage',
        items: this.getDailyLatestItems(),
        series: [
          { label: 'Total Data (GB)', valueMapper: (item) => this.getOfflineAdjustedValue(item, item.data_total_24h_gb), color: '#2563eb' },
          { label: 'Data Downloaded (GB)', valueMapper: (item) => this.getDataUsageValue(item, 'in'), color: '#059669' },
          { label: 'Data Uploaded (GB)', valueMapper: (item) => this.getDataUsageValue(item, 'out'), color: '#ea580c' }
        ]
      })
    ].filter(Boolean);
  };

  updateRoute = ({ screen = this.state.screen, dateStart = this.state.dateStart, dateEnd = this.state.dateEnd }) => {
    const params = {
      screen: (screen || '').trim(),
      startDate: dateStart,
      endDate: dateEnd
    };
    const query = queryString.stringify(params);
    this.props.history.replace(`${this.routePath}${query ? `?${query}` : ''}`);
  };

  getDateRangeError = (dateStart, dateEnd) => {
    const start = moment(dateStart, 'YYYY-MM-DD', true);
    const end = moment(dateEnd, 'YYYY-MM-DD', true);
    if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
      return Helper.getString('screenHealthInvalidDateRange');
    }
    if (end.diff(start, 'days') + 1 > MAX_DATE_RANGE_DAYS) {
      return Helper.getString('screenHealthMaxDateRange').replace('%n%', MAX_DATE_RANGE_DAYS);
    }
    return '';
  };

  onFilter = ({ startTs, endTs, venueCode }) => {
    const screen = (venueCode || '').trim();
    if (!screen) {
      this.setState({ showToast: true, toastMsg: Helper.getString('screenHealthEnterScreenCode') });
      return;
    }
    if (!startTs || !endTs) {
      this.setState({ showToast: true, toastMsg: Helper.getString('screenHealthSelectDateRange') });
      return;
    }

    const timezone = AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO;
    const dateStart = moment(Number(startTs)).tz(timezone).format('YYYY-MM-DD');
    const dateEnd = moment(Number(endTs)).tz(timezone).format('YYYY-MM-DD');
    const dateRangeError = this.getDateRangeError(dateStart, dateEnd);
    if (dateRangeError) {
      this.setState({ showToast: true, toastMsg: dateRangeError });
      return;
    }

    this.setState({
      dateStart,
      dateEnd,
      screen,
      inProcessFilter: true,
      showToast: false,
      toastMsg: ''
    }, () => {
      this.updateRoute({ dateStart, dateEnd, screen });
      this.fetchReport({ inline: true });
    });
  };

  renderReportContent() {
    if (!this.state.screen) {
      if (this.embedded) return null;
      return (
        <VenueHealthDashboard
          key='venue-health-dashboard'
          {...this.props}
          embedded
        />
      );
    }

    if (this.state.inProcessFilter && !this.state.hasCompletedReportRequest) return null;

    const chartsData = this.buildChartsData();
    const { device } = this.state;
    return (
      <div className={css(Styles.mainContainer)}>
        {device &&
          <div className={css(Styles.sectionBlock)}>
            <div className={css(Styles.deviceSummaryCard)}>
              <VenueSummary2
                item={device}
                compact
                showNotRegistered={false}
              />
            </div>
          </div>
        }

        <div className={css(Styles.latencyNoteWrap)}>
          <div className={css(Styles.latencyNote)}>
            {Helper.getString('screenHealthReportLatencyNote')}
          </div>
        </div>

        <div className={css(Styles.sectionBlock)}>
          {chartsData.length > 0
            ? (
              <Grid className={css(Styles.chartsContainer)} container spacing={2}>
                {chartsData.map((chart, index) => (
                  <Grid item key={`line_chart_${index}`} className={css(Styles.eachChartHolder)} xs={12}>
                    <div className={css(Styles.chartOuterWrap)}>
                      <div className={css(Styles.chartBlock)}>
                        {chart.chartTitle &&
                          <div className={css(Styles.chartHeader)}>
                            <div className={css(Styles.chartTitleContainer)}>
                              <div className={css(Styles.chartTitle)}>{chart.chartTitle}</div>
                              <div className={css(Styles.timezoneBadge)}>CST</div>
                            </div>
                          </div>
                        }
                        <div className={css(Styles.chartCanvasHolder)}>
                          <Line
                            className={css(Styles.chartCanvas)}
                            data={chart.data}
                            options={chart.options}
                          />
                        </div>
                      </div>
                    </div>
                  </Grid>
                ))}
              </Grid>
            )
            : (
              <div className={css(Styles.noChartMessage)}>
                {Helper.getString('noChartDataMessage')}
              </div>
            )}
        </div>
      </div>
    );
  }

  renderAboveTable() {
    const { dateStart, dateEnd } = this.state;
    return (
      <div className={css(Styles.contentWrap)}>
        <div className={css(Styles.filtersWrap)}>
          <PlaybackHistoryFilterBar
            key={`${this.state.screen}-${dateStart}-${dateEnd}`}
            viewMode='ssp-only'
            startTs={moment.tz(dateStart, 'YYYY-MM-DD', AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO).valueOf()}
            endTs={moment.tz(dateEnd, 'YYYY-MM-DD', AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO).valueOf()}
            venueCode={this.state.screen}
            timezone={AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO}
            timezoneLabel={AppConfig.DEFAULT_TIMEZONE || AppConfig.DEFAULT_ZONEINFO}
            showHourFilters={false}
            buttonLabel='Filter'
            inProcess={this.state.inProcessFilter}
            onFilter={this.onFilter}
          />
          <Toast
            open={this.state.showToast}
            severity='error'
            message={this.state.toastMsg}
            handleClose={() => this.setState({ showToast: false })}
          />
          {/* <div className={css(Styles.samplingNote)}>
            Up to 24 data points are shown. Ranges of 1–4 days are sampled evenly; longer ranges use the latest daily entries and are evenly reduced when needed.
          </div> */}
        </div>
        {this.renderReportContent()}
      </div>
    );
  }
}

const Styles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    minWidth: 0
  },
  deviceSummaryCard: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '16px 18px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 8px 24px rgba(14, 30, 37, 0.06)'
  },
  sectionBlock: {
    width: '100%',
    marginBottom: 28
  },
  latencyNoteWrap: {
    margin: '4px 0 14px'
  },
  latencyNote: {
    fontSize: 14,
    color: '#040BC5'
  },
  chartsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    width: '100% !important',
    margin: '0 !important'
  },
  eachChartHolder: {
    display: 'flex'
  },
  chartOuterWrap: {
    background: '#ffffff',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden',
    width: '100%'
  },
  chartBlock: {
    maxHeight: '320px',
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 16,
    '@media (max-width: 767px)': {
      flexDirection: 'column'
    }
  },
  chartCanvas: {
    width: '100% !important',
    height: '100% !important'
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 4,
    color: '#0f172a'
  },
  chartTitleContainer: {
    display: 'flex !important',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },
  timezoneBadge: {
    fontSize: 12,
    fontWeight: 600,
    color: '#020BC3',
    backgroundColor: '#EEF2FF',
    borderRadius: 999,
    padding: '2px 8px',
    height: 'fit-content'
  },
  noChartMessage: {
    width: '100%',
    minHeight: 180,
    background: '#fff',
    borderRadius: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#6b7280'
  },
  chartCanvasHolder: {
    position: 'relative',
    width: '100%',
    height: 250,
    maxWidth: '100%',
    overflow: 'hidden',
    padding: '0 12px 8px 12px',
    boxSizing: 'border-box'
  },
  filtersWrap: {
    width: '100%',
    marginBottom: 12
  },
  contentWrap: {
    width: `calc(100vw - ${AppTheme.drawerWidth + (AppTheme.adminPagePadding * 2)}px)`,
    maxWidth: AppTheme.viewPort,
    minWidth: 0,
    boxSizing: 'border-box',
    '@media (max-width: 767px)': {
      width: 'calc(100vw - 20px)'
    }
  },
  samplingNote: {
    color: '#64748b',
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 6,
    textAlign: 'right'
  }
});



// WEBPACK FOOTER //
// ./src/screens/admin/ScreenHealthTimeseriesReport.js